import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { type NotificationOutput } from '@1financial/shared';
import { useAuth, getAccessToken, authenticatedFetch } from '../lib/auth-context.js';
import { fmtDateTime } from '../lib/date.js';

export function NotificationBell() {
  const { api } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const bellRef = useRef<HTMLDivElement>(null);

  const list = useQuery({
    queryKey: ['notifications', { unreadOnly: true, limit: 10 }],
    enabled: api !== null,
    refetchInterval: 30000,
    queryFn: async () => {
      const res = await authenticatedFetch('/v1/notifications?unreadOnly=true&limit=10', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: NotificationOutput[]; total: number } };
      return body.data;
    },
  });

  // Real-time: SSE refreshes the bell instantly. Browser notifications are
  // delivered only by the service worker through the BrowserPushToggle and
  // admin policy — never as a side effect of merely having the bell open.
  useEffect(() => {
    if (!api) return;
    const ctrl = new AbortController();
    let stopped = false;
    let retry: ReturnType<typeof setTimeout> | null = null;

    function onNewNotification() {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }

    async function connect() {
      try {
        const res = await authenticatedFetch('/v1/notifications/stream', {
          headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
          signal: ctrl.signal,
        });
        if (!res.ok || !res.body) {
          if (res.status === 401 || res.status === 403) stopped = true;
          throw new Error(`stream ${res.status}`);
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          let idx: number;
          while ((idx = buf.indexOf('\n\n')) >= 0) {
            const chunk = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            const dataLine = chunk.split('\n').find((l) => l.startsWith('data: '));
            if (dataLine) {
              try { JSON.parse(dataLine.slice(6)); onNewNotification(); } catch { /* ignore malformed event */ }
            }
          }
        }
      } catch (error) {
        if (ctrl.signal.aborted || (error instanceof Error && error.name === 'AbortError')) stopped = true;
      }
      if (!stopped) retry = setTimeout(() => void connect(), 3000);
    }

    void connect();
    return () => {
      stopped = true;
      if (retry) clearTimeout(retry);
      ctrl.abort();
    };
  }, [api, queryClient]);

  const markAllRead = useMutation({
    mutationFn: async () => {
      const res = await authenticatedFetch('/v1/notifications/mark-all-read', {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const unreadCount = list.data?.total ?? 0;
  const items = list.data?.items ?? [];

  return (
    <div className="relative" ref={bellRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 text-muted hover:text-white transition-colors"
        aria-label="Notifications"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 h-4 min-w-[16px] px-1 rounded-full bg-rose-500 text-[10px] font-semibold text-oncolor flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-bg-panel border border-border rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="text-sm font-semibold text-white">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllRead.mutate()}
                className="text-xs text-accent hover:text-emerald-400"
                disabled={markAllRead.isPending}
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 && (
              <p className="px-4 py-6 text-sm text-muted text-center">No notifications</p>
            )}
            {items.map((n) => (
              <Link
                key={n.id}
                to={n.actionUrl ?? '/notifications'}
                onClick={() => setOpen(false)}
                className="block px-4 py-3 border-b border-border/50 hover:bg-bg-hover/40 transition-colors"
              >
                <p className="text-sm text-white font-medium">{n.title}</p>
                <p className="text-xs text-muted line-clamp-2 mt-0.5">{n.body}</p>
                <p className="text-[11px] text-muted mt-1">{fmtDateTime(n.createdAt)}</p>
              </Link>
            ))}
          </div>
          <div className="px-4 py-2 border-t border-border text-center">
            <Link to="/notifications" onClick={() => setOpen(false)} className="text-xs text-accent hover:text-emerald-400">
              View all notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
